// Ansicht Daten.
//
// CSV-Dateien hochladen, ihre Spalten + Beispielzeilen anzeigen und die Spalten grafisch den
// Katalog-Eingängen zuordnen (Zwei-Spalten-Mapper: je CSV-Spalte eine Auswahl des Eingangs-Tags).
// Die Zeitstempel-Spalte steuert später den Takt. Gespeichert wird die Zuordnung per PUT
// data-sources — die CSV-Dateien selbst liegen im NICHT git-versionierten Datenverzeichnis der
// Instanz und werden nicht mit ausgeliefert.

import { useCallback, useEffect, useMemo, useRef, useState, type JSX } from 'react';
import { useI18n } from '../i18n';
import { useStore } from '../state/store';
import {
  deleteDataFile,
  getDataSources,
  getFileColumns,
  listDataFiles,
  putDataSources,
  uploadDataFile,
} from '../api/client';
import type {
  CsvEndBehavior,
  DataFileColumns,
  DataFileInfo,
  DataSource,
  DataSources,
} from '../api/types';
import { Button, EmptyState, LabeledSelect, PanelHeading } from '../components/ui';

// Leitet aus einem Dateinamen eine stabile, kebab-artige Quellen-ID ab (für data-sources.json).
function sourceIdFor(file: string): string {
  return (
    file
      .replace(/\.csv$/i, '')
      .toLowerCase()
      .replace(/[^a-z0-9]+/g, '-')
      .replace(/^-+|-+$/g, '') || 'quelle'
  );
}

export function DataView(): JSX.Element {
  const { t } = useI18n();
  const { activeProjectId, catalog, notify, reportError } = useStore();
  const fileInputRef = useRef<HTMLInputElement>(null);

  const [files, setFiles] = useState<DataFileInfo[]>([]);
  const [selected, setSelected] = useState<string | null>(null);
  const [columns, setColumns] = useState<DataFileColumns | null>(null);
  const [dataSources, setDataSources] = useState<DataSources>({ schema_version: 1, sources: [] });
  const [busy, setBusy] = useState(false);

  // Der aktuell bearbeitete Zuordnungs-Zustand je gewählter Datei.
  const [timestampColumn, setTimestampColumn] = useState<string>('');
  const [colMap, setColMap] = useState<Record<string, string>>({});
  const [endBehavior, setEndBehavior] = useState<CsvEndBehavior>('stop');

  // Katalog-Eingänge (direction === 'input') sind die zuordenbaren Ziel-Tags.
  const inputTags = useMemo(
    () => (catalog?.variables ?? []).filter((v) => v.direction === 'input').map((v) => v.tag),
    [catalog],
  );

  // Dateiliste + gespeicherte Zuordnung beim Projektwechsel laden.
  const reload = useCallback(
    async (projectId: string) => {
      try {
        const [list, ds] = await Promise.all([
          listDataFiles(projectId),
          getDataSources(projectId),
        ]);
        setFiles(list.files);
        setDataSources(ds);
      } catch (err) {
        reportError(err, t('data.loadFailed'));
      }
    },
    [reportError, t],
  );

  useEffect(() => {
    if (!activeProjectId) {
      setFiles([]);
      setSelected(null);
      setColumns(null);
      setDataSources({ schema_version: 1, sources: [] });
      return;
    }
    void reload(activeProjectId);
  }, [activeProjectId, reload]);

  // Beim Datei-Wechsel: Spalten laden und den Zuordnungs-Zustand aus einer ggf. gespeicherten
  // Quelle vorbelegen (sonst leer).
  useEffect(() => {
    if (!activeProjectId || !selected) {
      setColumns(null);
      return;
    }
    let cancelled = false;
    void getFileColumns(activeProjectId, selected)
      .then((cols) => {
        if (cancelled) return;
        setColumns(cols);
        const stored = dataSources.sources.find((s) => s.file === selected);
        setColMap(stored?.columnMap ?? {});
        setTimestampColumn(stored?.timestampColumn ?? cols.timestampCandidates[0] ?? '');
        setEndBehavior(stored?.endBehavior ?? 'stop');
      })
      .catch((err) => !cancelled && reportError(err, t('data.columnsFailed')));
    return () => {
      cancelled = true;
    };
  }, [activeProjectId, selected, dataSources, reportError, t]);

  const onUpload = useCallback(
    async (file: File) => {
      if (!activeProjectId) return;
      setBusy(true);
      try {
        const info = await uploadDataFile(activeProjectId, file);
        notify('success', t('data.uploaded', { name: info.name }));
        await reload(activeProjectId);
        setSelected(info.name);
      } catch (err) {
        reportError(err, t('data.uploadFailed'));
      } finally {
        setBusy(false);
        if (fileInputRef.current) fileInputRef.current.value = '';
      }
    },
    [activeProjectId, notify, reload, reportError, t],
  );

  const onRemove = useCallback(
    async (name: string) => {
      if (!activeProjectId) return;
      setBusy(true);
      try {
        await deleteDataFile(activeProjectId, name);
        notify('info', t('data.removed'));
        if (selected === name) setSelected(null);
        await reload(activeProjectId);
      } catch (err) {
        reportError(err, t('data.removeFailed'));
      } finally {
        setBusy(false);
      }
    },
    [activeProjectId, notify, reload, reportError, selected, t],
  );

  // Die Zuordnung der gewählten Datei in data-sources.json einpflegen (ersetzt eine vorhandene
  // Quelle gleicher Datei) und speichern (PUT).
  const onSave = useCallback(async () => {
    if (!activeProjectId || !selected || !columns) return;
    // Nur nicht-leere Zuordnungen (Spalte → gewählter Tag) übernehmen.
    const columnMap: Record<string, string> = {};
    for (const [col, tag] of Object.entries(colMap)) {
      if (tag) columnMap[col] = tag;
    }
    const source: DataSource = {
      id: sourceIdFor(selected),
      file: selected,
      timestampColumn: timestampColumn || null,
      columnMap,
      endBehavior,
    };
    const others = dataSources.sources.filter((s) => s.file !== selected);
    const next: DataSources = { schema_version: 1, sources: [...others, source] };
    setBusy(true);
    try {
      const res = await putDataSources(activeProjectId, next);
      setDataSources(next);
      notify('success', t('data.saved', { rev: res.rev }));
    } catch (err) {
      reportError(err, t('data.saveFailed'));
    } finally {
      setBusy(false);
    }
  }, [
    activeProjectId,
    selected,
    columns,
    colMap,
    timestampColumn,
    endBehavior,
    dataSources,
    notify,
    reportError,
    t,
  ]);

  if (!activeProjectId) {
    return (
      <div className="p-8">
        <EmptyState title={t('data.empty')} hint={t('data.emptyHint')} />
      </div>
    );
  }

  return (
    <div className="flex h-full flex-col overflow-hidden" data-testid="data-view">
      <div className="border-b border-bf-border px-6 py-3">
        <PanelHeading>{t('data.heading')}</PanelHeading>
        <p className="max-w-3xl text-xs text-bf-muted">{t('data.intro')}</p>
      </div>

      <div className="grid min-h-0 flex-1 grid-cols-[16rem_1fr] overflow-hidden">
        {/* Dateiliste + Upload */}
        <aside className="flex flex-col overflow-y-auto border-r border-bf-border p-4">
          <PanelHeading>{t('data.files')}</PanelHeading>
          <input
            ref={fileInputRef}
            type="file"
            accept=".csv,text/csv"
            data-testid="data-upload-input"
            className="hidden"
            onChange={(e) => {
              const f = e.target.files?.[0];
              if (f) void onUpload(f);
            }}
          />
          <Button
            variant="primary"
            disabled={busy}
            onClick={() => fileInputRef.current?.click()}
          >
            {busy ? t('data.uploading') : t('data.upload')}
          </Button>

          <div className="mt-3 flex flex-col gap-1">
            {files.length === 0 && <p className="text-xs text-bf-muted">{t('data.noFiles')}</p>}
            {files.map((f) => (
              <div
                key={f.name}
                data-testid={`data-file-${f.name}`}
                className={
                  'flex items-center justify-between rounded border px-2 py-1.5 text-sm ' +
                  (selected === f.name
                    ? 'border-bf-gold text-bf-white'
                    : 'border-bf-border text-bf-muted hover:text-bf-white')
                }
              >
                <button
                  type="button"
                  className="flex-1 truncate text-left"
                  onClick={() => setSelected(f.name)}
                >
                  {f.name}
                </button>
                <button
                  type="button"
                  aria-label={t('data.remove')}
                  title={t('data.remove')}
                  className="ml-2 text-xs text-red-300 hover:text-red-200"
                  onClick={() => void onRemove(f.name)}
                >
                  ✕
                </button>
              </div>
            ))}
          </div>
        </aside>

        {/* Spalten + Zuordnung */}
        <section className="min-h-0 overflow-y-auto p-6">
          {!selected || !columns ? (
            <EmptyState title={t('data.selectFile')} />
          ) : (
            <div className="flex flex-col gap-6">
              {/* Spalten + Beispielzeilen */}
              <div>
                <PanelHeading>{t('data.columns')}</PanelHeading>
                <div className="overflow-x-auto rounded border border-bf-border">
                  <table className="w-full text-left text-xs" data-testid="data-columns-table">
                    <thead>
                      <tr className="border-b border-bf-border text-bf-muted">
                        {columns.columns.map((c) => (
                          <th key={c} className="px-2 py-1.5 font-semibold">
                            {c}
                          </th>
                        ))}
                      </tr>
                    </thead>
                    <tbody>
                      {columns.sampleRows.map((row, i) => (
                        <tr key={i} className="border-b border-bf-border/50 text-bf-white">
                          {columns.columns.map((c) => (
                            <td key={c} className="px-2 py-1 tabular-nums">
                              {row[c] ?? ''}
                            </td>
                          ))}
                        </tr>
                      ))}
                    </tbody>
                  </table>
                </div>
              </div>

              {/* Zeitstempel-Spalte */}
              <div className="max-w-sm">
                <LabeledSelect
                  label={t('data.timestampColumn')}
                  value={timestampColumn}
                  onChange={setTimestampColumn}
                >
                  <option value="">{t('data.noTimestamp')}</option>
                  {columns.columns.map((c) => (
                    <option key={c} value={c}>
                      {c}
                    </option>
                  ))}
                </LabeledSelect>
              </div>

              {/* Grafische Zuordnung: je CSV-Spalte ein Katalog-Eingang */}
              <div>
                <PanelHeading>{t('data.mapping')}</PanelHeading>
                <p className="mb-3 max-w-2xl text-xs text-bf-muted">{t('data.mappingHint')}</p>
                {inputTags.length === 0 ? (
                  <p className="text-xs text-amber-300">{t('data.noInputs')}</p>
                ) : (
                  <div className="flex flex-col gap-2">
                    {/* Kopf des Zwei-Spalten-Mappers */}
                    <div className="grid grid-cols-2 gap-3 text-xs font-semibold text-bf-muted">
                      <span>{t('data.csvColumn')}</span>
                      <span>{t('data.catalogInput')}</span>
                    </div>
                    {columns.columns
                      .filter((c) => c !== timestampColumn)
                      .map((c) => (
                        <div
                          key={c}
                          className="grid grid-cols-2 items-center gap-3"
                          data-testid={`data-map-${c}`}
                        >
                          <span className="truncate text-sm text-bf-white">{c}</span>
                          <select
                            aria-label={c}
                            value={colMap[c] ?? ''}
                            onChange={(e) =>
                              setColMap((prev) => ({ ...prev, [c]: e.target.value }))
                            }
                            className="w-full rounded border border-bf-border bg-bf-navy px-2 py-1.5 text-sm text-bf-white outline-none focus:border-bf-gold"
                          >
                            <option value="">{t('data.unassigned')}</option>
                            {inputTags.map((tag) => (
                              <option key={tag} value={tag}>
                                {tag}
                              </option>
                            ))}
                          </select>
                        </div>
                      ))}
                  </div>
                )}
              </div>

              <div>
                <Button variant="primary" disabled={busy} onClick={() => void onSave()}>
                  {t('data.save')}
                </Button>
              </div>
            </div>
          )}
        </section>
      </div>
    </div>
  );
}
